import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link as RouterLink } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { selectProfile } from "../../users/reducer"
import DialogHeader from "../components/DialogHeader"
import { retrieveEmailAuthorizationStatus } from "../reducer"
import {
  mailboxCreatePayload,
  mailboxSuggestPayload,
} from "./zohoMailboxIdentity"

function firstApiErrorMessage(data, fallback) {
  if (!data || typeof data !== "object") {
    return hideVendorName(fallback)
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return hideVendorName(data.detail)
  }
  for (const key of [
    "email",
    "first_name",
    "owner_user_id",
    "assigned_user_id",
    "non_field_errors",
    "detail",
  ]) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) {
      return hideVendorName(value)
    }
    if (Array.isArray(value) && value.length) {
      return hideVendorName(value.map(String).join(" "))
    }
  }
  return hideVendorName(fallback)
}

function hideVendorName(text) {
  return String(text || "")
    .replace(/\bZoho Mail\b/gi, "company email")
    .replace(/\bZoho\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^[:\-–—.\s]+/, "")
    .trim()
}

function oauthStatusColor(status) {
  if (status === "connected") return "success"
  if (status === "reconnect_required") return "warning"
  if (status === "pending") return "info"
  return "default"
}

export default function ConciergeZohoMailboxCard({ onStatusMessage }) {
  const profile = useSelector(selectProfile)
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()
  const [orgStatus, setOrgStatus] = useState(null)
  const [mailbox, setMailbox] = useState(null)
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState("")
  const [statusAlert, setStatusAlert] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)
  const [passwordDialog, setPasswordDialog] = useState(null)

  const notify = useCallback(
    (message, severity = "info") => {
      setStatusAlert(message ? { message, severity } : null)
      if (onStatusMessage && message) {
        onStatusMessage(message, severity)
      }
    },
    [onStatusMessage]
  )

  const loadState = useCallback(() => {
    setIsLoading(true)
    return Promise.all([
      axios.get("/crm/api/zoho-mail/org/status/"),
      axios.get("/crm/api/zoho-mail/mailboxes/"),
    ])
      .then(([orgRes, listRes]) => {
        setOrgStatus(orgRes.data)
        const mine = (listRes.data || []).find(
          (row) => row.assigned_user_id === profile?.id
        )
        const first = mine || (listRes.data || [])[0] || null
        setMailbox(first)
        if (first?.email) {
          setEmail(first.email)
          return null
        }
        return axios
          .post(
            "/crm/api/zoho-mail/mailboxes/suggest/",
            mailboxSuggestPayload()
          )
          .then((suggestRes) => {
            if (suggestRes.data?.email) {
              setEmail(suggestRes.data.email)
            }
          })
          .catch(() => {})
      })
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Could not load mailbox status."
          ),
          "error"
        )
      })
      .finally(() => setIsLoading(false))
  }, [axios, notify, profile?.id, profile?.active_account])

  useEffect(() => {
    loadState()
  }, [loadState])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("zoho") === "connected") {
      notify("Mailbox connected successfully.", "success")
      loadState()
      dispatch(retrieveEmailAuthorizationStatus())
    } else if (params.get("zoho") === "error") {
      notify(params.get("message") || "Mailbox connection failed.", "error")
    }
  }, [dispatch, loadState, notify])

  function handleSuggest() {
    setIsSuggesting(true)
    setEmailError("")
    axios
      .post("/crm/api/zoho-mail/mailboxes/suggest/", mailboxSuggestPayload())
      .then((res) => {
        setEmail(res.data.email)
        notify(`Suggested ${res.data.email}`, "info")
      })
      .catch((error) => {
        const message = firstApiErrorMessage(
          error?.response?.data,
          "Could not suggest an email."
        )
        setEmailError(message)
        notify(message, "error")
      })
      .finally(() => setIsSuggesting(false))
  }

  function handleCreate() {
    setIsCreating(true)
    setEmailError("")
    axios
      .post("/crm/api/zoho-mail/mailboxes/validate/", { email })
      .then(() =>
        axios.post("/crm/api/zoho-mail/mailboxes/", mailboxCreatePayload(email))
      )
      .then((res) => {
        setMailbox(res.data)
        if (res.data?.temporary_password) {
          setPasswordDialog({
            email: res.data.email,
            temporaryPassword: res.data.temporary_password,
            notice:
              res.data.password_notice ||
              "Copy this password now. It is not stored in the CRM.",
          })
        } else {
          notify(
            `Mailbox ${res.data.email} created. Connect OAuth to send and sync.`,
            "success"
          )
        }
      })
      .catch((error) => {
        const message = firstApiErrorMessage(
          error?.response?.data,
          "Could not create mailbox."
        )
        setEmailError(message)
        notify(message, "error")
      })
      .finally(() => setIsCreating(false))
  }

  function handleCopyPassword() {
    if (!passwordDialog?.temporaryPassword) return
    navigator.clipboard
      .writeText(passwordDialog.temporaryPassword)
      .then(() => notify("Password copied to clipboard.", "success"))
      .catch(() =>
        notify(
          "Could not copy automatically — select and copy manually.",
          "warning"
        )
      )
  }

  function handleClosePasswordDialog() {
    setPasswordDialog(null)
    notify("Use this password when you sign in during Connect mailbox.", "info")
  }

  function handleShowSignInPassword() {
    if (!mailbox?.id) return
    axios
      .post(`/crm/api/zoho-mail/mailboxes/${mailbox.id}/reset-password/`)
      .then((res) => {
        setPasswordDialog({
          email: res.data.email,
          temporaryPassword: res.data.temporary_password,
          notice:
            res.data.password_notice ||
            res.data.detail ||
            "Copy this password now. It is not stored in the CRM.",
        })
      })
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Could not generate a sign-in password."
          ),
          "error"
        )
      })
  }

  function handleConnect() {
    if (!mailbox?.id) return
    setIsConnecting(true)
    axios
      .post(`/crm/api/zoho-mail/mailboxes/${mailbox.id}/connect/`)
      .then((res) => {
        const url = res.data?.authorization_url
        if (url) {
          window.location.href = url
          return
        }
        notify("Missing authorization URL from server.", "error")
      })
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Could not start mailbox connection."
          ),
          "error"
        )
      })
      .finally(() => setIsConnecting(false))
  }

  function handleTest() {
    if (!mailbox?.id) return
    setIsTesting(true)
    axios
      .post(`/crm/api/zoho-mail/mailboxes/${mailbox.id}/test/`)
      .then(() => notify("Mailbox connection test succeeded.", "success"))
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Connection test failed."
          ),
          "error"
        )
      })
      .finally(() => setIsTesting(false))
  }

  function handleDisable() {
    if (!mailbox?.id) return
    setIsDisabling(true)
    axios
      .post(`/crm/api/zoho-mail/mailboxes/${mailbox.id}/disable/`)
      .then((res) => {
        setMailbox(res.data)
        notify("Mailbox disabled.", "success")
      })
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Could not disable mailbox."
          ),
          "error"
        )
      })
      .finally(() => setIsDisabling(false))
  }

  const orgConnected = Boolean(orgStatus?.is_connected)
  const canCreate =
    !mailbox || mailbox.status === "disabled" || mailbox.status === "error"

  return (
    <>
      <Dialog
        open={Boolean(passwordDialog)}
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader
          title="Mailbox password"
          onClose={handleClosePasswordDialog}
        />
        <DialogContent>
          <Column sx={{ gap: 2, pt: 1 }}>
            <Alert severity="warning">
              {passwordDialog?.notice ||
                "This password is shown once and is not stored in the CRM."}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Mailbox
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {passwordDialog?.email}
            </Typography>
            <TextField
              label="One-time password"
              value={passwordDialog?.temporaryPassword || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              onFocus={(event) => event.target.select()}
            />
            <Typography variant="body2" color="text.secondary">
              When you click Connect mailbox, sign in with the mailbox email
              above and this password. You may be asked to set a new password on
              first login.
            </Typography>
          </Column>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCopyPassword}>Copy password</Button>
          <Button variant="contained" onClick={handleClosePasswordDialog}>
            I saved it — continue
          </Button>
        </DialogActions>
      </Dialog>

      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardHeader
          avatar={
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(137, 47, 206, 0.1)",
                color: "primary.main",
              }}
            >
              <EmailOutlinedIcon />
            </Box>
          }
          title="Company mailbox"
          subheader="Create a dedicated @onebasecrm.com address for this parent. Concierge-only — owners cannot read inbound mail."
          titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
          subheaderTypographyProps={{
            variant: "body2",
            sx: { mt: 0.5, maxWidth: 560 },
          }}
          action={
            <Button
              variant="outlined"
              component={RouterLink}
              to="/messages"
              startIcon={<InboxOutlinedIcon />}
              sx={{ mt: 0.5, whiteSpace: "nowrap" }}
            >
              Open Messages
            </Button>
          }
          sx={{
            textAlign: "left",
            alignItems: "flex-start",
            pb: 1,
            "& .MuiCardHeader-content": { overflow: "visible" },
            "& .MuiCardHeader-action": { m: 0 },
          }}
        />
        <CardContent>
          <Column sx={{ gap: 2 }}>
            {statusAlert && (
              <Alert severity={statusAlert.severity}>
                {statusAlert.message}
              </Alert>
            )}
            {isLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading mailbox status…
              </Typography>
            ) : (
              <>
                <Row sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <Chip
                    size="small"
                    label={
                      orgConnected
                        ? `Org connected (${orgStatus?.domain || "domain"})`
                        : "Org not connected"
                    }
                    color={orgConnected ? "success" : "warning"}
                  />
                  {mailbox && (
                    <Chip
                      size="small"
                      label={`OAuth: ${mailbox.oauth_status}`}
                      color={oauthStatusColor(mailbox.oauth_status)}
                    />
                  )}
                  {mailbox && (
                    <Chip size="small" label={`Status: ${mailbox.status}`} />
                  )}
                  {(mailbox?.owner_user_name ||
                    mailbox?.owner_user_email ||
                    profile?.account_owner_name) && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={`Parent: ${
                        mailbox?.owner_user_name ||
                        mailbox?.owner_user_email ||
                        profile?.account_owner_name
                      }`}
                    />
                  )}
                </Row>

                {!orgConnected && (
                  <Alert severity="warning">
                    Email organization is not connected yet. Ask OneBase staff
                    to connect it before creating a mailbox.
                  </Alert>
                )}

                {mailbox && mailbox.status !== "disabled" ? (
                  <Column sx={{ gap: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {mailbox.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {mailbox.display_name || "Concierge mailbox"}
                      {mailbox.owner_user_name || mailbox.owner_user_email
                        ? ` · Parent: ${
                            mailbox.owner_user_name || mailbox.owner_user_email
                          }`
                        : profile?.account_owner_name
                        ? ` · Parent: ${profile.account_owner_name}`
                        : ""}
                      {mailbox.forwarding_address
                        ? ` · Forwards to ${mailbox.forwarding_address}`
                        : ""}
                      {mailbox.last_error
                        ? ` · ${hideVendorName(mailbox.last_error)}`
                        : ""}
                    </Typography>
                    <Row sx={{ gap: 1, flexWrap: "wrap" }}>
                      {(mailbox.oauth_status === "pending" ||
                        mailbox.oauth_status === "reconnect_required" ||
                        mailbox.oauth_status === "disconnected") && (
                        <>
                          <LoadingButton
                            variant="contained"
                            loading={isConnecting}
                            onClick={handleConnect}
                          >
                            {mailbox.oauth_status === "reconnect_required"
                              ? "Reconnect mailbox"
                              : "Connect mailbox"}
                          </LoadingButton>
                          <Button
                            variant="outlined"
                            onClick={handleShowSignInPassword}
                          >
                            Get sign-in password
                          </Button>
                        </>
                      )}
                      {mailbox.oauth_status === "connected" && (
                        <LoadingButton
                          variant="outlined"
                          loading={isTesting}
                          onClick={handleTest}
                        >
                          Test connection
                        </LoadingButton>
                      )}
                      <LoadingButton
                        color="error"
                        variant="outlined"
                        loading={isDisabling}
                        onClick={handleDisable}
                      >
                        Disable mailbox
                      </LoadingButton>
                      <Button variant="text" onClick={loadState}>
                        Refresh
                      </Button>
                    </Row>
                  </Column>
                ) : (
                  <Column sx={{ gap: 1.5 }}>
                    <Row sx={{ gap: 1, alignItems: "flex-start" }}>
                      <TextField
                        label="Company email"
                        size="small"
                        fullWidth
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value.trim().toLowerCase())
                          setEmailError("")
                        }}
                        error={!!emailError}
                        helperText={
                          emailError ||
                          `Must use @${orgStatus?.domain || "onebasecrm.com"}`
                        }
                      />
                      <LoadingButton
                        variant="outlined"
                        loading={isSuggesting}
                        onClick={handleSuggest}
                        sx={{ whiteSpace: "nowrap", mt: 0.25 }}
                      >
                        Suggest
                      </LoadingButton>
                    </Row>
                    <LoadingButton
                      variant="contained"
                      loading={isCreating}
                      disabled={!orgConnected || !canCreate || !email}
                      onClick={handleCreate}
                    >
                      Create mailbox
                    </LoadingButton>
                  </Column>
                )}
              </>
            )}
          </Column>
        </CardContent>
      </Card>
    </>
  )
}
